import { CircleQuestionMarkIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { CottonCandyContainer } from 'pages/employer_onboarding/quote/CottonCandyContainer'
import { IconButton, Table } from 'slab'
import { convertGrapeMoneyToNumber, formatMoney } from 'utils'
import {
  type CombinedInvoice,
  formatInvoiceDate,
  formatInvoiceTitle,
  getDefaultYear,
  getInvoiceDate,
  INVOICE_TYPE,
  type InvoiceType,
} from 'utils/billing'
import {
  useGetEmployersEmployerInvoices,
  useGetEmployersEmployerInvoicesFirstBillingCycleStatus,
  useGetEmployersEmployerInvoicesStripeInvoices,
  useGetEmployersEmployerInvoicesYears,
} from 'utils/web_api'
import {
  type EmployersEmployerInvoice,
  type EmployersStripeInvoice,
} from 'utils/web_api.schemas'

import BillingBreakdown from './BillingBreakdown'
import { BillingYearDropdown } from './BillingYearDropdown'
import { InvoiceStatusBadge } from './InvoiceStatusBadge'
import { FirstBillModal } from './modals/FirstBillModal'
import { WhenDoesBillingHappenModal } from './modals/WhenDoesBillingHappenModal'

const SKELETON_ROWS = Array.from({ length: 5 }, (_, index) => (
  <Table.Row key={`skeleton-${index}`}>
    <Table.Cell>
      <SkeletonItem widthClass='w-full' />
    </Table.Cell>
    <Table.Cell>
      <SkeletonItem widthClass='w-1/2' />
    </Table.Cell>
    <Table.Cell>
      <SkeletonItem widthClass='w-20' />
    </Table.Cell>
    <Table.Cell>
      <SkeletonItem widthClass='w-24' />
    </Table.Cell>
    <Table.Cell>
      <SkeletonItem widthClass='w-16' />
    </Table.Cell>
  </Table.Row>
))

function BillingPage () {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false)
  const [isFirstBillModalOpen, setIsFirstBillModalOpen] = useState(false)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null)
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<InvoiceType | null>(null)

  const [isFirstBillModalDismissed, setIsFirstBillModalDismissed] = useLocalStorage(
    'thatch-billing-first-bill-modal-dismissed-v1',
    false
  )

  const {
    data: yearsData,
    isLoading: isYearsLoading,
  } = useGetEmployersEmployerInvoicesYears()

  const { data: firstBillingCycleStatus } = useGetEmployersEmployerInvoicesFirstBillingCycleStatus()

  const { data, isLoading, isError } = useGetEmployersEmployerInvoices({
    year: selectedYear,
  })

  const {
    data: stripeInvoicesData,
    isLoading: isStripeInvoicesLoading,
    isError: isStripeInvoicesError,
  } = useGetEmployersEmployerInvoicesStripeInvoices({
    year: selectedYear,
  })

  const allInvoices: EmployersEmployerInvoice[] = data?.invoices || []
  const stripeInvoices: EmployersStripeInvoice[] = stripeInvoicesData?.invoices || []
  const allYears: number[] = yearsData?.years || [currentYear]

  const isInFirstBillingCycle = firstBillingCycleStatus?.in_first_billing_cycle === true

  const shouldShowReopenButton = isInFirstBillingCycle && isFirstBillModalDismissed && !isFirstBillModalOpen

  const combinedInvoices = useMemo<CombinedInvoice[]>(() => {
    const employerInvoicesWithType: CombinedInvoice[] = allInvoices.map((invoice) => ({
      ...invoice,
      type: INVOICE_TYPE.EMPLOYER,
    }))
    const stripeInvoicesWithType: CombinedInvoice[] = stripeInvoices.map((invoice) => ({
      ...invoice,
      type: INVOICE_TYPE.STRIPE,
    }))
    const combined = [...employerInvoicesWithType, ...stripeInvoicesWithType]
    return combined.sort((a, b) => {
      const dateA = getInvoiceDate(a)
      const dateB = getInvoiceDate(b)
      return dateB.getTime() - dateA.getTime()
    })
  }, [allInvoices, stripeInvoices])

  useEffect(() => {
    if (!isYearsLoading && allYears.length > 0) {
      setSelectedYear((prevYear) => {
        if (!allYears.includes(prevYear)) {
          return getDefaultYear(allYears, currentYear, prevYear)
        }
        return prevYear
      })
    }
  }, [allYears, currentYear, isYearsLoading])

  useEffect(() => {
    if (isInFirstBillingCycle && !isFirstBillModalDismissed) {
      setIsFirstBillModalOpen(true)
    }
  }, [isFirstBillModalDismissed, isInFirstBillingCycle])

  const handleYearChange = (value: string) => {
    setSelectedYear(Number(value))
  }

  if (isError || isStripeInvoicesError) {
    return (
      <div>
        <div className='mt-2 mb-4 p-4 text-sm text-slate-600 border-2 border-red-500 rounded-lg shadow-md'>
          There was an error fetching invoices. Please reach out to{' '}
          <a href='mailto:support@thatch.com'>support@thatch.com</a> if you
          continue to experience this error.
        </div>
      </div>
    )
  }

  if (selectedInvoiceId && selectedInvoiceType) {
    return (
      <BillingBreakdown
        invoiceId={selectedInvoiceId}
        invoiceType={selectedInvoiceType}
        onBack={() => {
          setSelectedInvoiceId(null)
          setSelectedInvoiceType(null)
        }}
      />
    )
  }

  return (
    <div>
      <div className='mb-6'>
        <h1 className='heading-lg'>Billing</h1>
      </div>

      <div className='mb-6'>
        <CottonCandyContainer>
          <div className='p-6'>
            <p>ToDos go here</p>
          </div>
        </CottonCandyContainer>
      </div>

      <div className='mb-4 flex items-center gap-4 mt-10'>
        <h3 className='heading-sm'>All bills</h3>
        <BillingYearDropdown
          yearsAvailable={allYears}
          selectedYear={selectedYear}
          handleSetYear={handleYearChange}
          isLoading={isYearsLoading}
        />
      </div>

      <div className='mb-6 rounded-2xl shadow-[0_1px_4px_rgba(0,0,0,0.1)] border border-gray-200 overflow-hidden bg-white py-4'>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.ColumnHeader>Bill</Table.ColumnHeader>
              <Table.ColumnHeader>Date billed</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Invoice #</Table.ColumnHeader>
              <Table.ColumnHeader>Cost</Table.ColumnHeader>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {isLoading || isStripeInvoicesLoading
              ? (
                  SKELETON_ROWS
                )
              : combinedInvoices.length === 0
                ? (
                  <Table.Row>
                    <Table.Cell colSpan={5}>
                      <div className='text-center text-gray-500 py-8'>
                        No invoices found for {selectedYear}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                  )
                : (
                    combinedInvoices.map((invoice: CombinedInvoice) => {
                      const invoiceDate = getInvoiceDate(invoice)
                      const isEmployerInvoice = invoice.type === INVOICE_TYPE.EMPLOYER

                      return (
                        <Table.Row
                          key={invoice.id}
                          onClick={() => {
                            if (isEmployerInvoice) {
                              setSelectedInvoiceId(invoice.id)
                              setSelectedInvoiceType(INVOICE_TYPE.EMPLOYER)
                            } else {
                              setSelectedInvoiceId(invoice.id)
                              setSelectedInvoiceType(INVOICE_TYPE.STRIPE)
                            }
                          }}
                          accessibleLabel={`View details for ${formatInvoiceTitle(invoice)}`}
                        >
                          <Table.RowHeader>{formatInvoiceTitle(invoice)}</Table.RowHeader>
                          <Table.Cell>
                            {formatInvoiceDate(invoiceDate)}
                          </Table.Cell>
                          <Table.Cell>
                            <InvoiceStatusBadge invoice={invoice} />
                          </Table.Cell>
                          <Table.Cell>
                            {invoice.invoice_number || invoice.id}
                          </Table.Cell>
                          <Table.Cell>
                            {formatMoney(convertGrapeMoneyToNumber(invoice.total))}
                          </Table.Cell>
                        </Table.Row>
                      )
                    })
                  )}
          </Table.Body>
          <tfoot>
            <tr>
              <td colSpan={5} className='border-0 p-6'>
                <button
                  type='button'
                  onClick={() => {
                    window.segment?.track('[ER] When will I be billed clicked', {})
                    setIsBillingModalOpen(true)
                  }}
                  className='text-thatch-purple-secondary pb-0 inline-block underline decoration-dotted hover:decoration-solid underline-offset-[0.25rem] cursor-help'
                >
                  <span>When will I be billed?</span>
                </button>
              </td>
            </tr>
          </tfoot>
        </Table>
      </div>

      <WhenDoesBillingHappenModal
        open={isBillingModalOpen}
        onOpenChange={setIsBillingModalOpen}
      />

      <FirstBillModal
        open={isFirstBillModalOpen}
        onOpenChange={(open) => {
          setIsFirstBillModalOpen(open)
          if (!open) setIsFirstBillModalDismissed(true)
        }}
      />

      {shouldShowReopenButton && (
        <div className='fixed bottom-6 right-6 z-[100]'>
          <IconButton
            icon={CircleQuestionMarkIcon}
            label='Open first bill guide'
            onClick={() => setIsFirstBillModalOpen(true)}
            size='large'
            variant='primary'
          />
        </div>
      )}
    </div>
  )
}

export default BillingPage
